"use client";

import { useEffect } from "react";
import Link from "next/link";
import { useAuth } from "./AuthProvider";
import ProfileSettingsForm from "./ProfileSettingsForm";

/**
 * Profile-settings overlay shown over the current page. Closes via the X, the
 * backdrop, or Escape.
 */
export default function SettingsModal({ onClose }: { onClose: () => void }) {
  const { user } = useAuth();

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-[90] flex items-center justify-center bg-ocean-950/70 p-4 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
        className="relative max-h-[90vh] w-full max-w-md overflow-y-auto rounded-2xl border border-sky-900/60 bg-ocean-900/95 p-6 shadow-2xl"
      >
        <button
          onClick={onClose}
          aria-label="Close"
          className="absolute right-3 top-3 flex h-7 w-7 items-center justify-center rounded-full text-lg text-sky-200/70 hover:bg-ocean-800 hover:text-foam"
        >
          ×
        </button>

        <img src="/logo.webp" alt="PodLog" width={224} height={80} className="mx-auto mb-4 h-10 w-auto object-contain drop-shadow-md" />

        <h2 className="mb-4 text-center text-xl font-semibold text-foam">Profile settings</h2>

        {user ? (
          <ProfileSettingsForm user={user} />
        ) : (
          <p className="text-sm text-sky-200/70">
            Please sign in to edit your profile.{" "}
            <Link href="/auth/login" className="text-sky-300 hover:underline">
              Sign in
            </Link>
          </p>
        )}
      </div>
    </div>
  );
}
